import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Layout from '@/components/Layout'
import AdminDialog from '@/components/AdminDialog'
import { openFromRow } from '@/lib/openFromRow'
import { FilterSelect, DateTimeFilter } from '@/components/FilterDropdown'
import { billingApi, type Order, type Refund } from '@/api/billing'
import { errorMessage } from '@/api/client'
const names: Record<string, string> = {
  pending: '待支付',
  paid: '已支付',
  cancelled: '已关闭',
  refunded: '已退款',
  requested: '待审核',
  processing: '退款中',
  completed: '已退款',
  failed: '退款失败',
  rejected: '已拒绝',
}
const money = (n: number) => '¥ ' + (n / 100).toFixed(2)
const time = (s?: string) => (s ? new Date(s).toLocaleString() : '—')
export default function Orders() {
  const cache = useQueryClient()
  const [search] = useSearchParams()
  const empty = { keyword: '', status: '', creditMissing: '', from: '', to: '' }
  const [input, setInput] = useState(empty)
  const [filters, setFilters] = useState<Record<string, string>>(empty)
  const [page, setPage] = useState(1)
  const [tab, setTab] = useState(search.get('tab') || 'orders')
  const [detail, setDetail] = useState<string>()
  useEffect(() => {
    setTab(search.get('tab') === 'refunds' ? 'refunds' : 'orders')
    setDetail(search.get('detail') || undefined)
  }, [search])
  const [action, setAction] = useState<{
    order?: Order
    refund?: Refund
    type: string
  }>()
  const [reason, setReason] = useState('')
  const [notice, setNotice] = useState<{ text: string; id: number }>()
  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(undefined), 3200)
    return () => window.clearTimeout(timer)
  }, [notice])
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)
  const orders = useQuery({
    queryKey: ['billing-orders', page, filters],
    queryFn: () => billingApi.orders(page, filters),
    enabled: tab === 'orders',
  })
  const refunds = useQuery({
    queryKey: ['billing-refunds'],
    queryFn: billingApi.refunds,
    enabled: tab === 'refunds',
  })
  const selected = useQuery({
    queryKey: ['billing-order', detail],
    queryFn: () => billingApi.order(detail!),
    enabled: !!detail,
  })
  const mutation = useMutation({
    mutationFn: async () => {
      if (action?.type === 'repair-credit' && action.order) {
        return billingApi.repairCredit(action.order.id, reason)
      }
      if (action?.order) return billingApi.reconcile(action.order.id, reason)
      return billingApi.review(
            action!.refund!.id,
            action!.type,
            reason,
            action!.refund!.revision,
          )
    },
    onSuccess: (result) => {
      setNotice({
        id: Date.now(),
        text: action?.type === 'repair-credit'
          ? `已补记 ${(result as { pointsDelta: number }).pointsDelta.toLocaleString()} 点；账本记录已生成。`
          : action?.order
          ? `查单完成：${names[(result as Order).status] || (result as Order).status}。仅渠道确认支付成功后自动入账。`
          : action?.type === 'reject'
            ? '退款申请已拒绝，预留积分已释放。'
            : '审核结果已保存，退款到账以渠道最终结果为准。',
      })
      setAction(undefined)
      void cache.invalidateQueries({ queryKey: ['billing-orders'] })
      void cache.invalidateQueries({ queryKey: ['billing-refunds'] })
      void cache.invalidateQueries({ queryKey: ['billing-order'] })
      void cache.invalidateQueries({ queryKey: ['billing-notifications'] })
      void cache.invalidateQueries({ queryKey: ['admin-teams'] })
      void cache.invalidateQueries({ queryKey: ['admin-team-health'] })
    },
  })
  function choose(e: { currentTarget: HTMLElement }, next: typeof action) {
    openFromRow(e)
    setAction(next)
    setReason('')
    mutation.reset()
  }
  return (
    <Layout>
      <h1 className="mb-2 text-2xl font-semibold">订单管理</h1>
      <p className="mb-4 text-sm text-slate-500">
        查询用户充值订单、核对支付与处理退款申请。
      </p>
      <div role="tablist" aria-label="订单管理视图" className="mb-4 flex gap-2">
        {[
          ['orders', '充值订单'],
          ['refunds', '退款申请'],
        ].map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            className={
              'rounded-lg px-4 py-2 text-sm ' +
              (tab === id ? 'bg-brand-600 text-white' : 'border')
            }
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {notice && createPortal(
        <p key={notice.id} role="status" className="package-save-notice">
          <span aria-hidden="true" className="package-save-notice-icon">✓</span>
          {notice.text}
        </p>, document.body
      )}
      {error && (
        <p role="alert" className="mb-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {tab === 'orders' ? (
        <>
          <form
            className="mb-4 flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              setError('')
              if (input.from && input.to && input.from >= input.to) {
                setError('结束时间必须晚于开始时间')
                return
              }
              setFilters({
                ...input,
                keyword: input.keyword.trim(),
                from: input.from ? new Date(input.from).toISOString() : '',
                to: input.to ? new Date(input.to).toISOString() : '',
              })
              setPage(1)
            }}
          >
            <label className="min-w-48 flex-1 text-xs text-slate-600">
              订单 / 用户
              <input
                className="mt-1 h-10 w-full rounded-lg border px-3 text-sm"
                maxLength={200}
                placeholder="订单号、用户 ID、PPID 或昵称"
                value={input.keyword}
                onChange={(e) =>
                  setInput({ ...input, keyword: e.target.value })
                }
              />
            </label>
            <FilterSelect
              label="订单状态"
              value={input.status}
              onChange={(status) => setInput({ ...input, status })}
              options={[
                ['', '全部状态'],
                ...['pending', 'paid', 'cancelled', 'refunded'].map(
                  (k) => [k, names[k]] as [string, string],
                ),
              ]}
            />
            <FilterSelect
              label="入账情况"
              value={input.creditMissing}
              onChange={(creditMissing) => setInput({ ...input, creditMissing })}
              options={[["", "全部"], ["true", "已支付未入账"]]}
            />
            <DateTimeFilter
              label="开始时间"
              value={input.from}
              onChange={(from) => setInput({ ...input, from })}
            />
            <DateTimeFilter
              label="结束时间（不含）"
              value={input.to}
              onChange={(to) => setInput({ ...input, to })}
            />
            <button className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white">
              查询
            </button>
            <button
              type="button"
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={() => {
                setInput(empty)
                setFilters(empty)
                setPage(1)
                setError('')
                void orders.refetch()
              }}
            >
              重置
            </button>
            <button
              type="button"
              disabled={exporting}
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={async () => {
                setExporting(true)
                setError('')
                try {
                  await billingApi.export(filters)
                } catch (e) {
                  setError(errorMessage(e))
                } finally {
                  setExporting(false)
                }
              }}
            >
              {exporting ? '导出中…' : '导出 CSV'}
            </button>
          </form>
          {orders.isError && (
            <p role="alert" className="text-red-700">
              {errorMessage(orders.error)}{' '}
              <button onClick={() => void orders.refetch()}>重试</button>
            </p>
          )}
          <div className="min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead className="sticky top-0 bg-slate-100 text-slate-600">
                <tr>
                  {[
                    '订单编号 / 时间',
                    '用户',
                    '套餐 / 到账',
                    '金额',
                    '状态',
                    '操作',
                  ].map((x) => (
                    <th key={x} className="p-3">
                      {x}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.data?.items.map((o) => (
                  <tr key={o.id} className="border-t">
                    <td className="p-3">
                      <p className="font-mono text-xs">{o.orderNo}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {time(o.createdAt)}
                      </p>
                    </td>
                    <td className="p-3">
                      <p>{o.userName || '—'}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {o.ppid || o.userId}
                      </p>
                    </td>
                    <td className="p-3">
                      <p>{o.productTitle}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {o.points.toLocaleString()} 积分
                      </p>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {money(o.amountFen)}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <span
                        className={
                          o.status === 'paid'
                            ? 'text-green-700'
                            : o.status === 'pending'
                              ? 'text-amber-700'
                              : 'text-slate-500'
                        }
                      >
                        {names[o.status] || o.status}
                      </span>
                      {o.creditMissing && <span className="ml-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900">未入账</span>}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <button
                        className="text-brand-600 hover:underline"
                        onClick={(e) => {
                          openFromRow(e)
                          setDetail(o.id)
                        }}
                      >
                        详情
                      </button>
                      {o.paymentMethod === 'wechat' &&
                        o.status === 'pending' && (
                          <button
                            className="ml-4 text-brand-600 hover:underline"
                            onClick={(e) =>
                              choose(e, { order: o, type: 'reconcile' })
                            }
                          >
                            查单补单
                          </button>
                        )}
                      {o.creditRepairable && (
                        <button
                          className="ml-4 text-amber-800 hover:underline"
                          onClick={(e) => choose(e, { order: o, type: 'repair-credit' })}
                        >
                          核实并补记
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.isPending ? (
              <p className="p-8 text-center text-slate-500">加载中…</p>
            ) : (
              orders.data?.items.length === 0 && (
                <p className="p-8 text-center text-slate-500">暂无匹配订单</p>
              )
            )}
          </div>
          <div className="mt-4 flex justify-between text-sm">
            <span>
              共 {orders.data?.total || 0} 条 · 第 {page} 页
            </span>
            <div className="flex gap-4">
              <button
                disabled={page === 1 || orders.isFetching}
                className="disabled:opacity-30"
                onClick={() => setPage(page - 1)}
              >
                上一页
              </button>
              <button
                disabled={
                  page * 20 >= (orders.data?.total || 0) || orders.isFetching
                }
                className="disabled:opacity-30"
                onClick={() => setPage(page + 1)}
              >
                下一页
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 flex justify-between text-sm text-slate-500">
            <p>显示最近 200 条退款申请，审核操作留存日志。</p>
            <button
              onClick={() => void refunds.refetch()}
              className="text-brand-600"
            >
              刷新
            </button>
          </div>
          {refunds.isError && (
            <p role="alert" className="text-red-700">
              {errorMessage(refunds.error)}
            </p>
          )}
          <div className="min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="sticky top-0 bg-slate-100 text-slate-600">
                <tr>
                  {[
                    '订单 / 申请时间',
                    '套餐',
                    '退款金额',
                    '申请原因',
                    '状态',
                    '审核',
                  ].map((x) => (
                    <th className="p-3" key={x}>
                      {x}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {refunds.data?.items.map((r) => (
                  <tr
                    className={
                      'border-t ' +
                      (search.get('refundOrder') === r.orderNo
                        ? 'bg-brand-50'
                        : '')
                    }
                    key={r.id}
                  >
                    <td className="p-3">
                      <p className="font-mono text-xs">{r.orderNo}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {time(r.requestedAt)}
                      </p>
                    </td>
                    <td className="p-3">{r.productTitle}</td>
                    <td className="p-3">{money(r.amountFen)}</td>
                    <td className="max-w-60 break-words p-3">
                      {r.reason}
                      <p className="text-xs text-slate-500">{r.adminNotes}</p>
                    </td>
                    <td className="p-3">{names[r.status] || r.status}</td>
                    <td className="p-3 whitespace-nowrap">
                      {['requested', 'failed', 'processing'].includes(
                        r.status,
                      ) ? (
                        <>
                          <button
                            className="text-brand-600"
                            onClick={(e) =>
                              choose(e, { refund: r, type: 'approve' })
                            }
                          >
                            {r.status === 'processing'
                              ? '核对退款'
                              : '同意退款'}
                          </button>
                          {r.status === 'requested' && (
                            <button
                              className="ml-4 text-red-700"
                              onClick={(e) =>
                                choose(e, { refund: r, type: 'reject' })
                              }
                            >
                              拒绝
                            </button>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {refunds.isPending ? (
              <p className="p-8 text-center text-slate-500">加载中…</p>
            ) : (
              refunds.data?.items.length === 0 && (
                <p className="p-8 text-center text-slate-500">暂无退款申请</p>
              )
            )}
          </div>
        </>
      )}
      {detail && (
        <AdminDialog title="订单详情" onClose={() => setDetail(undefined)}>
          {selected.isPending ? (
            <p>加载中…</p>
          ) : selected.isError ? (
            <p role="alert" className="text-red-700">
              {errorMessage(selected.error)}
            </p>
          ) : (
            selected.data && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-4 text-sm">
                {Object.entries({
                  订单编号: selected.data.orderNo,
                  '用户 ID': selected.data.userId,
                  工作区: selected.data.workspaceId,
                  套餐: selected.data.productTitle,
                  支付金额: money(selected.data.amountFen),
                  到账积分: selected.data.points.toLocaleString(),
                  状态: names[selected.data.status] || selected.data.status,
                  支付渠道: selected.data.paymentMethod,
                  微信交易号: selected.data.providerOrderId || '—',
                  创建时间: time(selected.data.createdAt),
                  到期时间: time(selected.data.expiresAt),
                  支付时间: time(selected.data.paidAt),
                }).map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="break-all">{v}</dd>
                  </div>
                ))}
              </dl>
            )
          )}
        </AdminDialog>
      )}
      {action && (
        <AdminDialog
          title={
            action.type === 'repair-credit'
              ? '核实支付并补记点数'
              : action.order
              ? '查单补单'
              : action.type === 'approve'
                ? '确认退款'
                : '拒绝退款'
          }
          busy={mutation.isPending}
          onClose={() => setAction(undefined)}
        >
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              mutation.mutate()
            }}
          >
            <p className="break-all text-sm font-medium">
              {action.order?.orderNo || action.refund?.orderNo}
            </p>
            <p className="text-sm text-slate-500">
              {action.type === 'repair-credit'
                ? '系统将再次查询微信支付，核对交易号、金额和时间，并确认没有原入账流水。充值包只补记一次；订阅订单仅在原周期仍有效且没有后续订阅、消费或冻结时补记，否则需人工复核。'
                : action.order
                ? '向微信查询真实支付状态。只有已支付订单会自动入账，重复核对不会重复加积分。'
                : action.type === 'approve'
                  ? `确认原路退回 ${money(action.refund!.amountFen)}，仅在支付渠道完成退款后更新到账状态。`
                  : '拒绝申请后将释放预留积分，请填写明确原因。'}
            </p>
            <label className="block text-sm">
              操作原因
              <textarea
                required
                minLength={2}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-2 h-24 w-full rounded-lg border p-3"
              />
            </label>
            {mutation.isError && (
              <p role="alert" className="text-sm text-red-700">
                {errorMessage(mutation.error)}
              </p>
            )}
            <button
              disabled={mutation.isPending}
              className="block ml-auto rounded-lg bg-brand-600 px-4 py-2 text-sm text-white"
            >
              {mutation.isPending ? '处理中…' : '确认操作'}
            </button>
          </form>
        </AdminDialog>
      )}
    </Layout>
  )
}
